import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProject, getProjectSelections, updateSelectionStatus, getProjectStats, getProjectComments, deleteComment } from "../api";
import UploadView from "../components/UploadView";
import CuratorView from "../components/CuratorView";
import BookView from "../components/BookView";
import MagicDesigner from "../components/MagicDesigner";
import ShareLinkManager from "../components/ShareLinkManager";
import SelectionViewer from "../components/SelectionViewer";
import { Upload, Eye, LayoutGrid, Sparkles, Share2, Star, BarChart3, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ProjectWorkspace = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upload");
  const [selections, setSelections] = useState([]);
  const [activeSelection, setActiveSelection] = useState(null);
  const [stats, setStats] = useState(null);
  const [projectComments, setProjectComments] = useState([]);

  useEffect(() => {
    loadProject();
  }, [projectId]);

  useEffect(() => {
    if (activeTab === "selections") loadSelections();
    if (activeTab === "stats") { loadStats(); loadProjectComments(); }
  }, [activeTab, projectId]);

  const loadProject = async () => {
    try {
      const data = await getProject(projectId);
      setProject(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSelections = async () => {
    try {
      const data = await getProjectSelections(projectId);
      setSelections(data);
    } catch {
      console.error("Failed to load selections");
    }
  };

  const loadStats = async () => {
    try {
      const data = await getProjectStats(projectId);
      setStats(data);
    } catch {}
  };

  const loadProjectComments = async () => {
    try {
      const data = await getProjectComments(projectId);
      setProjectComments(data);
    } catch {}
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      loadProjectComments();
      toast.success("Komentarz usuniety");
    } catch {
      toast.error("Blad usuwania");
    }
  };

  const handleStatusChange = async (selectionId, status) => {
    try {
      await updateSelectionStatus(selectionId, status);
      loadSelections();
      toast.success("Status zaktualizowany");
    } catch {
      toast.error("Blad aktualizacji statusu");
    }
  };

  if (loading) return <div className="text-center py-20">Loading workspace...</div>;
  if (!project) return <div className="text-center py-20">Project not found</div>;

  const tabs = [
    { id: "upload", label: "Upload", icon: Upload },
    { id: "curator", label: "Curator", icon: Eye },
    { id: "design", label: "Magic Designer", icon: Sparkles },
    { id: "book", label: "Book Editor", icon: LayoutGrid },
    { id: "sharing", label: "Udostepnianie", icon: Share2 },
    { id: "selections", label: "Selekcje", icon: Star, badge: selections.length || null },
    { id: "stats", label: "Statystyki", icon: BarChart3 },
  ];

  return (
    <div className="pb-20 h-screen flex flex-col">
      <div className="mb-4 border-b border-slate-200 pb-4 px-6 pt-4 flex-shrink-0 bg-white">
        <h1 className="text-2xl font-bold text-slate-900">{project.name}</h1>
        <p className="text-slate-500">{project.photos?.length || 0} photos total</p>
      </div>

      <div className="flex gap-2 mb-4 px-6 overflow-x-auto flex-shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-indigo-600 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? "bg-white/20" : "bg-pink-100 text-pink-600"
              }`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-slate-50 flex-1 overflow-hidden relative">
        {activeTab === "upload" && (
          <div className="p-6 overflow-y-auto h-full">
            <UploadView projectId={projectId} onUploadComplete={loadProject} />
          </div>
        )}
        {activeTab === "curator" && (
          <div className="p-6 overflow-y-auto h-full">
            <CuratorView projectId={projectId} photos={project.photos || []} onUpdate={loadProject} />
          </div>
        )}
        {activeTab === "design" && (
          <MagicDesigner projectId={projectId} project={project} onUpdate={loadProject} />
        )}
        {activeTab === "book" && (
          <div className="p-6 overflow-y-auto h-full">
            <BookView projectId={projectId} photos={project.photos || []} onUpdate={loadProject} />
          </div>
        )}
        {activeTab === "sharing" && (
          <div className="p-6 overflow-y-auto h-full">
            <ShareLinkManager projectId={projectId} />
          </div>
        )}
        {activeTab === "selections" && (
          <div className="p-6 overflow-y-auto h-full">
            {activeSelection ? (
              <div>
                <button
                  onClick={() => setActiveSelection(null)}
                  className="text-sm text-indigo-600 hover:underline mb-4"
                >
                  ← Powrot do listy
                </button>
                <SelectionViewer
                  selection={activeSelection}
                  photos={project.photos || []}
                  onStatusChange={handleStatusChange}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Selekcje klientow ({selections.length})
                </h3>
                {selections.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Star className="w-8 h-8 mx-auto mb-2" />
                    <p>Brak selekcji. Udostepnij galerie z wlaczonym wyborem zdjec.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selections.map((sel) => (
                      <button
                        key={sel.id}
                        onClick={() => setActiveSelection(sel)}
                        className="w-full bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between hover:border-indigo-300 transition-colors text-left"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{sel.client_name}</p>
                          <p className="text-sm text-slate-500">{sel.client_email}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {sel.selected_photo_ids?.length || 0} zdjec | {new Date(sel.created_at).toLocaleDateString("pl-PL")}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            sel.status === "approved"
                              ? "bg-green-100 text-green-700"
                              : sel.status === "reviewed"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {sel.status === "approved" ? "Zatwierdzona" : sel.status === "reviewed" ? "Przejrzana" : "Przeslana"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {activeTab === "stats" && (
          <div className="p-6 overflow-y-auto h-full space-y-6">
            <h3 className="text-lg font-semibold text-slate-900">Statystyki galerii</h3>
            {stats ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-indigo-600">{stats.total_views}</p>
                    <p className="text-sm text-slate-500">Wyswietlenia</p>
                  </div>
                  <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{stats.total_downloads}</p>
                    <p className="text-sm text-slate-500">Pobrania</p>
                  </div>
                  <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-pink-600">{stats.selection_count}</p>
                    <p className="text-sm text-slate-500">Selekcje</p>
                  </div>
                  <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{stats.comment_count}</p>
                    <p className="text-sm text-slate-500">Komentarze</p>
                  </div>
                </div>
                {stats.per_link?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Per link</h4>
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-slate-500">
                            <th className="px-4 py-2">Token</th>
                            <th className="px-4 py-2 text-right">Views</th>
                            <th className="px-4 py-2 text-right">Downloads</th>
                            <th className="px-4 py-2 text-right">ZIP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.per_link.map((s) => (
                            <tr key={s.token} className="border-b border-slate-50">
                              <td className="px-4 py-2 font-mono text-xs">{s.token}</td>
                              <td className="px-4 py-2 text-right">{s.views || 0}</td>
                              <td className="px-4 py-2 text-right">{s.downloads || 0}</td>
                              <td className="px-4 py-2 text-right">{s.zip_downloads || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-slate-400">Ladowanie statystyk...</p>
            )}

            {/* Comments */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Komentarze ({projectComments.length})
              </h4>
              {projectComments.length === 0 ? (
                <p className="text-sm text-slate-400">Brak komentarzy</p>
              ) : (
                <div className="space-y-2">
                  {projectComments.map((c) => {
                    const photo = project.photos?.find((p) => p.id === c.photo_id);
                    return (
                      <div key={c.id} className="bg-white rounded-lg border border-slate-200 p-3 flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-slate-900">{c.author_name}</span>
                            <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString("pl-PL")}</span>
                            {photo && <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{photo.filename}</span>}
                          </div>
                          <p className="text-sm text-slate-600">{c.text}</p>
                        </div>
                        <button onClick={() => handleDeleteComment(c.id)} className="text-red-400 hover:text-red-600 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectWorkspace;
